/**
 * Logica pura per le etichette di WhatsApp Business collegate alle etichette del CRM.
 * Testata in __tests__/wahaLabelsLogic.test.ts. Le chiamate (WAHA via proxy e DB) sono in
 * wahaLabels.ts.
 *
 * Stesse regole del core (flusia-core/src/domains/whatsapp/inbound/labelSync.ts), usate qui
 * dall'importazione iniziale:
 *   - etichetta WhatsApp gia' collegata -> si aggiorna il nome;
 *   - etichetta del CRM non collegata con lo stesso nome -> si collega (resta 'crm');
 *   - altrimenti se ne crea una nuova con origin 'whatsapp'.
 * Etichette senza config_id / waha_label_id = solo CRM: nessuna chiamata a WhatsApp.
 */

export const DEFAULT_LABEL_COLOR = '#6B7280';

export interface WahaLabel {
  id: string;
  name: string;
  color: string | null;
}

export interface LinkableLabel {
  id: string;
  name: string;
  color: string;
  config_id?: string | null;
  waha_label_id?: string | null;
  origin?: string | null;
}

export type ImportPlan =
  | { action: 'none'; id: string }
  | { action: 'update'; id: string; patch: { name?: string; color?: string } }
  | { action: 'adopt'; id: string; patch: { config_id: string; waha_label_id: string } }
  | { action: 'create'; row: { name: string; color: string; config_id: string; waha_label_id: string; origin: 'whatsapp' } };

/** Chat WhatsApp (e non Instagram/Messenger/altro): solo queste si sincronizzano. */
export function isWhatsAppChatJid(jid: string): boolean {
  return /@(c\.us|g\.us|lid|s\.whatsapp\.net)$/.test(jid);
}

/** Etichetta collegata a WhatsApp (ha sessione e id sul telefono). */
export function isLinkedLabel(label: LinkableLabel | undefined | null): label is LinkableLabel & { config_id: string; waha_label_id: string } {
  return !!label && !!label.config_id && !!label.waha_label_id;
}

/**
 * Si scrive sul telefono solo se l'etichetta e' collegata a WhatsApp Business dello
 * STESSO numero della chat aperta: un'etichetta del numero A assegnata da una chat
 * del numero B resta solo nel CRM (altrimenti finirebbe sul telefono di A).
 */
export function shouldSyncLabelToWhatsApp(
  label: LinkableLabel | undefined | null,
  chatJid: string,
  chatConfigId: string | null | undefined,
): boolean {
  return isLinkedLabel(label) && isWhatsAppChatJid(chatJid) && !!chatConfigId && label.config_id === chatConfigId;
}

function idOf(v: unknown): string | null {
  if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  if (typeof v === 'string' && v.trim()) return v.trim();
  if (v && typeof v === 'object') {
    const o = v as { _serialized?: unknown; id?: unknown };
    return idOf(o._serialized) ?? idOf(o.id);
  }
  return null;
}

function hexColor(v: unknown): string | null {
  if (typeof v !== 'string') return null;
  const m = /^#?([0-9a-fA-F]{6})$/.exec(v.trim());
  return m && m[1] ? `#${m[1].toUpperCase()}` : null;
}

/** Risposta di GET /api/{session}/labels -> etichette valide. */
export function parseWahaLabels(raw: unknown): WahaLabel[] {
  if (!Array.isArray(raw)) return [];
  const out: WahaLabel[] = [];
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue;
    const o = item as { id?: unknown; name?: unknown; colorHex?: unknown };
    const id = idOf(o.id);
    if (!id) continue;
    out.push({ id, name: typeof o.name === 'string' ? o.name.trim() : '', color: hexColor(o.colorHex) });
  }
  return out;
}

/** Risposta di GET /api/{session}/labels/{id}/chats -> id delle chat (string o {_serialized}). */
export function parseChatIds(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const ids = raw
    .map((c) => (c && typeof c === 'object' ? idOf((c as { id?: unknown }).id) : idOf(c)))
    .filter((id): id is string => !!id && id.includes('@'));
  return [...new Set(ids)];
}

/** @s.whatsapp.net -> @c.us (le chat del CRM sono salvate cosi'). */
export function toCrmChatJid(jid: string): string {
  return jid.endsWith('@s.whatsapp.net') ? `${jid.split('@')[0]}@c.us` : jid;
}

/**
 * Lista completa di id da mandare con PUT /api/{session}/labels/chats/{chatId}/ (WAHA
 * sostituisce tutte le etichette della chat): quelle attuali sul telefono piu' o meno una.
 * Cosi' non si perdono le etichette del telefono che il CRM non conosce.
 */
export function nextWahaLabelIds(current: readonly string[], labelId: string, add: boolean): string[] {
  const set = new Set(current);
  if (add) set.add(labelId);
  else set.delete(labelId);
  return [...set];
}

export function sameIdSet(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const s = new Set(a);
  return b.every((x) => s.has(x));
}

export function pickFreeLabelName(base: string, taken: ReadonlySet<string>): string {
  if (!taken.has(base)) return base;
  const first = `${base} (WhatsApp)`;
  if (!taken.has(first)) return first;
  for (let i = 2; i < 1000; i++) {
    const candidate = `${base} (WhatsApp ${i})`;
    if (!taken.has(candidate)) return candidate;
  }
  return `${base} (WhatsApp ${Date.now()})`;
}

export function planImportLabel(incoming: WahaLabel, configId: string, rows: readonly LinkableLabel[]): ImportPlan {
  const linked = rows.find((r) => r.config_id === configId && r.waha_label_id === incoming.id);
  const taken = new Set(rows.map((r) => r.name));
  if (linked) {
    const patch: { name?: string; color?: string } = {};
    if (incoming.name && incoming.name !== linked.name) {
      taken.delete(linked.name);
      patch.name = pickFreeLabelName(incoming.name, taken);
    }
    if (linked.origin === 'whatsapp' && incoming.color && incoming.color !== linked.color) patch.color = incoming.color;
    return Object.keys(patch).length ? { action: 'update', id: linked.id, patch } : { action: 'none', id: linked.id };
  }
  const name = incoming.name || `Etichetta WhatsApp ${incoming.id}`;
  const sameName = rows.find((r) => r.name === name && !r.config_id && !r.waha_label_id);
  if (sameName) return { action: 'adopt', id: sameName.id, patch: { config_id: configId, waha_label_id: incoming.id } };
  return {
    action: 'create',
    row: { name: pickFreeLabelName(name, taken), color: incoming.color ?? DEFAULT_LABEL_COLOR, config_id: configId, waha_label_id: incoming.id, origin: 'whatsapp' },
  };
}
